import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  Trophy,
  Target,
  TrendingUp,
  CheckCheck,
  ArrowLeft,
  Info,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  Sparkles,
  Calendar,
  Gift,
} from "lucide-react";
import { cn } from "@/utils/utils";
import { PageContainer } from "@/ui/components/layout/PageContainer";
import {
  NotificationService,
  type Notification,
} from "@/services/notification.service";
import { useNotification } from "@/controllers/hooks/useNotification";
import { supabase } from "@/infra/supabase/client";

// Notification type configurations
const notificationConfig: Record<
  string,
  {
    bg: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    color: string;
    label: string;
  }
> = {
  info: {
    bg: "bg-blue-100",
    icon: Info,
    color: "text-blue-600",
    label: "Info",
  },
  success: {
    bg: "bg-green-100",
    icon: CheckCircle,
    color: "text-green-600",
    label: "Sucesso",
  },
  warning: {
    bg: "bg-amber-100",
    icon: AlertTriangle,
    color: "text-amber-600",
    label: "Aviso",
  },
  error: {
    bg: "bg-red-100",
    icon: AlertCircle,
    color: "text-red-600",
    label: "Erro",
  },
  achievement: {
    bg: "bg-amber-100",
    icon: Trophy,
    color: "text-amber-600",
    label: "Conquistas",
  },
  level_up: {
    bg: "bg-purple-100",
    icon: TrendingUp,
    color: "text-purple-600",
    label: "Progressos",
  },
  temple_unlock: {
    bg: "bg-accent-100",
    icon: Target,
    color: "text-accent-700",
    label: "Templos",
  },
  system: {
    bg: "bg-muted",
    icon: Bell,
    color: "text-foreground-secondary",
    label: "Sistema",
  },
  reminder: {
    bg: "bg-blue-100",
    icon: Calendar,
    color: "text-blue-600",
    label: "Lembrete",
  },
  streak: {
    bg: "bg-orange-100",
    icon: Gift,
    color: "text-orange-600",
    label: "Sequência",
  },
};

// Helper function to get notification config with fallback
function getNotificationConfig(type: string) {
  return (
    notificationConfig[type] || {
      bg: "bg-muted",
      icon: Bell,
      color: "text-foreground-secondary",
      label: "Notificação",
    }
  );
}

type NotificationFilter =
  | "all"
  | "achievement"
  | "level_up"
  | "temple_unlock"
  | "system";

// Skeleton Loading Component
function NotificationSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="flex gap-3 bg-card rounded-xl p-4 border border-gray-100">
        <div className="w-10 h-10 bg-gray-200 rounded-full flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="h-3 bg-gray-200 rounded w-full" />
          <div className="h-3 bg-gray-200 rounded w-1/2" />
        </div>
      </div>
    </div>
  );
}

// Empty State Component
function EmptyState({ filter }: { readonly filter: NotificationFilter }) {
  const messages = {
    all: {
      icon: Bell,
      title: "Tudo em dia!",
      description:
        "Quando você desbloquear templos, subir de nível ou ganhar conquistas, as notificações aparecerão aqui",
      bgColor: "bg-accent-50",
      iconColor: "text-accent-600",
    },
    achievement: {
      icon: Trophy,
      title: "Nenhuma conquista ainda",
      description:
        "Continue completando tarefas para desbloquear conquistas incríveis!",
      bgColor: "bg-amber-50",
      iconColor: "text-amber-600",
    },
    level_up: {
      icon: TrendingUp,
      title: "Nenhum progresso recente",
      description: "Continue sua jornada para subir de nível e evoluir!",
      bgColor: "bg-purple-50",
      iconColor: "text-purple-600",
    },
    temple_unlock: {
      icon: Target,
      title: "Nenhum templo desbloqueado",
      description: "Avance no seu progresso para desbloquear novos templos!",
      bgColor: "bg-accent-50",
      iconColor: "text-accent-600",
    },
    system: {
      icon: Info,
      title: "Nenhuma notificação do sistema",
      description: "Você está atualizado com todas as informações!",
      bgColor: "bg-blue-50",
      iconColor: "text-blue-600",
    },
  };

  const config = messages[filter];
  const Icon = config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center py-20 px-4"
    >
      <div
        className={cn(
          "w-24 h-24 rounded-full flex items-center justify-center mb-6 relative",
          config.bgColor,
        )}
      >
        <Icon className={cn("w-12 h-12", config.iconColor)} />
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/0 to-white/50" />
      </div>
      <h3 className="text-xl font-bold text-foreground mb-2">{config.title}</h3>
      <p className="text-foreground-muted text-center max-w-lg text-sm leading-relaxed">
        {config.description}
      </p>
      <div className="mt-8 flex gap-2">
        <Sparkles className="w-5 h-5 text-accent-500 animate-pulse" />
        <Sparkles className="w-4 h-4 text-accent-400 animate-pulse delay-100" />
        <Sparkles className="w-5 h-5 text-accent-500 animate-pulse delay-200" />
      </div>
    </motion.div>
  );
}

export default function Notifications() {
  const queryClient = useQueryClient();
  const toast = useNotification();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<NotificationFilter>("all");
  const [userId, setUserId] = useState<string | null>(null);

  // Get current user
  useEffect(() => {
    const getCurrentUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
      } else {
        navigate("/login");
      }
    };
    getCurrentUser();
  }, [navigate]);

  // Fetch notifications
  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ["notifications", userId],
    queryFn: () =>
      userId
        ? NotificationService.getNotifications(userId, {
            limit: 100,
          })
        : Promise.resolve([]),
    enabled: !!userId,
    staleTime: 1000 * 60 * 2, // Cache por 2 minutos
  });

  // Fetch unread count
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ["unread-notifications", userId],
    queryFn: () =>
      userId ? NotificationService.getUnreadCount(userId) : Promise.resolve(0),
    enabled: !!userId,
    staleTime: 1000 * 60 * 2, // Cache por 2 minutos
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: () =>
      userId
        ? NotificationService.markAllAsRead(userId)
        : Promise.resolve(false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["unread-notifications"] });
      toast.success("Todas as notificações foram marcadas como lidas");
    },
  });

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: number) =>
      NotificationService.markAsRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["unread-notifications"] });
    },
  });

  // Filter notifications
  const filteredNotifications = useMemo(() => {
    if (filter === "all") return notifications;
    if (filter === "system") {
      return notifications.filter((n) =>
        ["info", "success", "warning", "error"].includes(n.type),
      );
    }
    return notifications.filter((n) => n.type === filter);
  }, [notifications, filter]);

  // Group by date
  const groupedNotifications = useMemo(() => {
    const groups: Record<string, Notification[]> = {
      hoje: [],
      ontem: [],
      "esta-semana": [],
      "mais-antigas": [],
    };

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    filteredNotifications.forEach((notification) => {
      const date = new Date(notification.created_at);
      if (date >= today) {
        groups.hoje.push(notification);
      } else if (date >= yesterday) {
        groups.ontem.push(notification);
      } else if (date >= weekAgo) {
        groups["esta-semana"].push(notification);
      } else {
        groups["mais-antigas"].push(notification);
      }
    });

    return groups;
  }, [filteredNotifications]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);

    if (minutes < 1) return "Agora";
    if (minutes < 60) return `há ${minutes}m`;
    if (hours < 24) return `há ${hours}h`;
    return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsReadMutation.mutate(notification.id);
    }
    // TODO: Navigate to related content based on notification type
  };

  const handleMarkAllAsRead = () => {
    if (unreadCount > 0) {
      markAllAsReadMutation.mutate();
    }
  };

  const dateLabels = {
    hoje: "Hoje",
    ontem: "Ontem",
    "esta-semana": "Esta Semana",
    "mais-antigas": "Mais Antigas",
  };

  return (
    <div className="min-h-screen bg-[#fafaf9]">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-sm border-b border-black/[0.06] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3.5">
          {/* Top bar with back button */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/dashboard")}
                className="w-8 h-8 rounded-lg bg-black/[0.03] border border-black/[0.06] flex items-center justify-center hover:bg-black/[0.06] transition-colors"
              >
                <ArrowLeft className="w-4 h-4 text-neutral-500" />
              </button>
              <h1 className="text-sm font-bold text-neutral-800">
                Notificações
              </h1>
            </div>

            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                disabled={markAllAsReadMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors text-sm font-medium disabled:opacity-50 shadow-sm"
              >
                <CheckCheck className="w-4 h-4" />
                <span className="hidden sm:inline">
                  Marcar todas como lidas
                </span>
                <span className="sm:hidden">Marcar todas</span>
              </button>
            )}
          </div>

          {/* Title */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-accent-600 to-accent-800 rounded-xl flex items-center justify-center shadow-lg">
              <Bell className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                Notificações
              </h1>
              {unreadCount > 0 && (
                <p className="text-sm text-foreground-muted">
                  {unreadCount} não {unreadCount === 1 ? "lida" : "lidas"}
                </p>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            <button
              onClick={() => setFilter("all")}
              className={cn(
                "px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap flex items-center gap-2",
                filter === "all"
                  ? "bg-gray-900 text-white shadow-md"
                  : "bg-muted text-foreground-secondary hover:bg-gray-200",
              )}
            >
              <Bell className="w-4 h-4" />
              Todas
            </button>
            <button
              onClick={() => setFilter("achievement")}
              className={cn(
                "px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap flex items-center gap-2",
                filter === "achievement"
                  ? "bg-amber-600 text-white shadow-md"
                  : "bg-amber-50 text-amber-700 hover:bg-amber-100",
              )}
            >
              <Trophy className="w-4 h-4" />
              Conquistas
            </button>
            <button
              onClick={() => setFilter("level_up")}
              className={cn(
                "px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap flex items-center gap-2",
                filter === "level_up"
                  ? "bg-purple-600 text-white shadow-md"
                  : "bg-purple-50 text-purple-700 hover:bg-purple-100",
              )}
            >
              <TrendingUp className="w-4 h-4" />
              Progressos
            </button>
            <button
              onClick={() => setFilter("temple_unlock")}
              className={cn(
                "px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap flex items-center gap-2",
                filter === "temple_unlock"
                  ? "bg-accent-700 text-white shadow-md"
                  : "bg-accent-50 text-accent-700 hover:bg-accent-100",
              )}
            >
              <Target className="w-4 h-4" />
              Templos
            </button>
            <button
              onClick={() => setFilter("system")}
              className={cn(
                "px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap flex items-center gap-2",
                filter === "system"
                  ? "bg-blue-600 text-white shadow-md"
                  : "bg-blue-50 text-blue-700 hover:bg-blue-100",
              )}
            >
              <Info className="w-4 h-4" />
              Sistema
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <PageContainer maxWidth="default">
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <NotificationSkeleton key={i} />
            ))}
          </div>
        ) : filteredNotifications.length === 0 ? (
          <EmptyState filter={filter} />
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedNotifications).map(
              ([group, groupNotifications]) => {
                if (groupNotifications.length === 0) return null;

                return (
                  <div key={group}>
                    {/* Date Header */}
                    <div className="flex items-center gap-2 mb-3">
                      <Calendar className="w-4 h-4 text-foreground-muted" />
                      <h2 className="text-xs font-semibold text-foreground-muted uppercase tracking-wider">
                        {dateLabels[group as keyof typeof dateLabels]}
                      </h2>
                      <div className="flex-1 h-px bg-gray-200" />
                    </div>

                    {/* Notifications */}
                    <div className="space-y-2">
                      <AnimatePresence mode="popLayout">
                        {groupNotifications.map((notification) => {
                          const config = getNotificationConfig(
                            notification.type,
                          );
                          const Icon = config.icon;

                          return (
                            <motion.div
                              key={notification.id}
                              layout
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, x: -100 }}
                              whileHover={{ scale: 1.01 }}
                              whileTap={{ scale: 0.99 }}
                              onClick={() =>
                                handleNotificationClick(notification)
                              }
                              className={cn(
                                "bg-card rounded-xl border border p-4 cursor-pointer transition-all duration-200 hover:shadow-md group relative overflow-hidden",
                                !notification.read &&
                                  "ring-2 ring-accent-200 shadow-sm",
                              )}
                            >
                              {/* Gradient overlay on hover */}
                              <div className="absolute inset-0 bg-gradient-to-r from-accent-50/0 to-accent-50/0 group-hover:from-accent-50/50 group-hover:to-transparent transition-all duration-300" />

                              <div className="flex gap-3 relative">
                                {/* Icon */}
                                <div
                                  className={cn(
                                    "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm",
                                    config.bg,
                                  )}
                                >
                                  <Icon
                                    className={cn("w-5 h-5", config.color)}
                                  />
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1">
                                      <p className="font-semibold text-foreground text-sm mb-0.5 leading-tight">
                                        {notification.title}
                                      </p>
                                      <p className="text-sm text-foreground-secondary leading-relaxed">
                                        {notification.message}
                                      </p>
                                    </div>
                                    {/* Unread indicator */}
                                    {!notification.read && (
                                      <div className="w-2.5 h-2.5 bg-accent-600 rounded-full mt-1.5 flex-shrink-0 shadow-md ring-2 ring-white" />
                                    )}
                                  </div>

                                  {/* Timestamp */}
                                  <div className="flex items-center gap-2 mt-2">
                                    <p className="text-xs text-foreground-muted font-medium">
                                      {formatDate(notification.created_at)}
                                    </p>
                                    {notification.data &&
                                      typeof notification.data === "object" &&
                                      "xp" in notification.data &&
                                      typeof notification.data.xp ===
                                        "number" && (
                                        <>
                                          <span className="text-gray-300">
                                            •
                                          </span>
                                          <span className="text-xs font-semibold text-purple-600 flex items-center gap-1">
                                            <Gift className="w-3 h-3" />+
                                            {notification.data.xp} XP
                                          </span>
                                        </>
                                      )}
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </AnimatePresence>
                    </div>
                  </div>
                );
              },
            )}
          </div>
        )}
      </PageContainer>

      {/* Bottom padding for mobile */}
      <div className="h-20" />
    </div>
  );
}
